/**
 * Market-canonical slugs for the voucher-redeem page.
 *
 * The redeem route is locale-specific: /verzilveren (NL markets), /einloesen
 * (DE), /echanger (FR markets), /redeem (UK), /wymien (PL). A visitor landing
 * on the "wrong" slug for the current market (e.g. /einloesen on favotrip.nl)
 * gets 301'd to the market-canonical slug so the breadcrumb + JSON-LD always
 * match the rendered path.
 *
 * Unlike the catalog slugs in slug-paths.js (which are LOCALE-keyed and handled
 * by localizeHref/localizePath), the redeem slug is MARKET-keyed — fr-BE and
 * fr-FR share the French slug, and it stays outside the central segment map on
 * purpose, exactly like the statutory withdrawal slug in withdrawal-paths.js.
 * So link call-sites resolve it directly from this map by marketKey rather than
 * through localizeHref (which would leave /verzilveren untouched and pay a 301
 * hop on every non-NL market).
 *
 * Consumed by:
 *   - src/middleware.js (301 wrong-slug -> market-canonical)
 *   - src/components/layout/Navbar.js (renders the canonical href directly,
 *     and uses isRedeemPath() below to force a hard <a> for that one item)
 *   - src/components/layout/Footer.js (same, for its own redeem link)
 *   - src/components/util/MarkdownLinkText.js (uses isRedeemPath() so a
 *     `[label](/verzilveren)` FAQ link also hard-navigates)
 *
 * Kept dependency-free on purpose: the edge middleware bundle and the client
 * components both import it (same constraint as withdrawal-paths.js).
 */
export const REDEEM_SLUG_BY_MARKET = {
  "nl-nl": "/verzilveren",
  "nl-be": "/verzilveren",
  "fr-be": "/echanger",
  "de-de": "/einloesen",
  "fr-fr": "/echanger",
  "en-gb": "/redeem",
  "pl-pl": "/wymien",
};

export const ALL_REDEEM_SLUGS = new Set([
  "/verzilveren",
  "/einloesen",
  "/echanger",
  "/redeem",
  "/wymien",
]);

/**
 * Resolve the redeem href for a market. Falls back to the Dutch slug for an
 * unknown market key so a link never renders empty; the middleware then 301s
 * it to the correct market slug as a safety net (same as the withdrawal link).
 */
export function redeemHrefForMarket(marketKey) {
  return REDEEM_SLUG_BY_MARKET[marketKey] || "/verzilveren";
}

/**
 * WP6c reviewronde 2: is this path (any market's redeem slug, with or
 * without a trailing sub-path or query/hash) one of the verzilverroutes?
 *
 * Every rendered ENTRY POINT into a redeem route (Navbar, Footer, the
 * markdown links in FAQ/vouchers copy) must go through this single check
 * so they all render a hard `<a href>` instead of next/link's `<Link>` —
 * layout.js's boot-time tracking-sensitive-path guard
 * (window.__trackingPixelIsSensitivePathNow / the inline GTM guard) only
 * re-runs on a real document load, not on a client-side SPA navigation.
 * A soft `<Link>` navigation onto a redeem page would leave a
 * already-resident GTM/Meta/TikTok container running with no guard for
 * the vouchercode-bearing destination.
 *
 * WP6c reviewronde 3 (Opus, punt 3): only matches a path-shaped string
 * ("/verzilveren", "/verzilveren?code=..."). An ABSOLUTE URL (e.g.
 * "https://www.favotrip.de/einloesen") does NOT match, by design -- this
 * check is a call-site guard for the site's own internal <Link>/<a>
 * hrefs, which are always relative on this codebase's call sites (see
 * REDEEM_SLUG_BY_MARKET/redeemHrefForMarket above and buildRedeemUrl in
 * redeem-url.js, both of which hand back a relative path for use as an
 * href). A caller that constructs a full absolute URL for some other
 * purpose (e.g. display text) must not assume isRedeemPath() will
 * recognize it.
 */
export function isRedeemPath(path) {
  if (typeof path !== "string" || !path) return false;
  const pathname = path.split(/[?#]/)[0];
  for (const slug of ALL_REDEEM_SLUGS) {
    if (pathname === slug || pathname.startsWith(`${slug}/`)) return true;
  }
  return false;
}
